import { useCallback, useEffect, useRef } from 'react'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useConversationsRequests } from '../../../../../services/apis/conversations/useConversationsRequests'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { useSharedInboxRequests } from '../../../../../services/apis/sharedInbox/useSharedInboxRequests'
import { useIsSharedInbox } from '../../../../../stores/inbox/InboxModeContext'

const POLL_INTERVAL_MS = 3000
const MAX_ATTEMPTS = 4

type TagStatusRow = {
    conversationId: string
    conversationTags: any[]
    conversationTagPending: boolean
}

export const useConversationTagPolling = () => {
    const { store: { conversations } } = useInboxStore()
    const isSharedInbox = useIsSharedInbox()
    const personalRequests = useConversationsRequests()
    const sharedRequests = useSharedInboxRequests()
    // Shared-inbox labels live on the shared rows; the personal endpoint would never see them resolve.
    const { getConversationsTagStatus } = isSharedInbox ? sharedRequests : personalRequests
    const { onConversationTagResolved } = useInboxHandlers()

    const pendingIdsRef = useRef<string[]>([])
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const attemptsRef = useRef<Record<string, number>>({})
    const getStatusRef = useRef(getConversationsTagStatus)
    const onResolvedRef = useRef(onConversationTagResolved)
    getStatusRef.current = getConversationsTagStatus
    onResolvedRef.current = onConversationTagResolved

    const applyTagStatusRows = useCallback((rows: TagStatusRow[]) => {
        for (const row of rows) {
            if (row.conversationTagPending) {
                attemptsRef.current[row.conversationId] = (attemptsRef.current[row.conversationId] ?? 0) + 1
                if (attemptsRef.current[row.conversationId] >= MAX_ATTEMPTS) {
                    pendingIdsRef.current = pendingIdsRef.current.filter(id => id !== row.conversationId)
                }
            } else {
                const resolved = onResolvedRef.current(row)
                if (resolved === false) {
                    attemptsRef.current[row.conversationId] = (attemptsRef.current[row.conversationId] ?? 0) + 1
                    if (attemptsRef.current[row.conversationId] >= MAX_ATTEMPTS) {
                        pendingIdsRef.current = pendingIdsRef.current.filter(id => id !== row.conversationId)
                    }
                } else {
                    delete attemptsRef.current[row.conversationId]
                }
            }
        }
    }, [])

    const pollPendingTags = useCallback(async () => {
        const ids = pendingIdsRef.current
        if (!ids.length) return

        try {
            const res = await getStatusRef.current(ids)
            applyTagStatusRows(res?.conversations ?? [])
        } catch {
            // silent — next tick will retry
        }
    }, [applyTagStatusRows])

    useEffect(() => {
        const allPendingIds: string[] = conversations
            .filter((c: any) => c.conversationTagPending === true)
            .map((c: any) => String(c.conversationId))

        const activeIds = allPendingIds.filter((id) => (attemptsRef.current[id] ?? 0) < MAX_ATTEMPTS)
        Object.keys(attemptsRef.current).forEach((id) => {
            if (!allPendingIds.includes(id)) delete attemptsRef.current[id]
        })

        const hadPending = pendingIdsRef.current.length > 0
        const gainedPending = activeIds.some((id) => !pendingIdsRef.current.includes(id))
        pendingIdsRef.current = activeIds

        if (activeIds.length > 0 && !intervalRef.current) {
            intervalRef.current = setInterval(() => {
                void pollPendingTags()
            }, POLL_INTERVAL_MS)
        } else if (activeIds.length === 0 && intervalRef.current) {
            clearInterval(intervalRef.current)
            intervalRef.current = null
        }

        if (activeIds.length > 0 && (!hadPending || gainedPending)) {
            void pollPendingTags()
        }
    }, [conversations, pollPendingTags])

    useEffect(() => {
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current)
        }
    }, [])
}
